<template>
  <div class="my-container"  aria-label="">
    <!-- ✅ Hero Section -->
    <header
      class="page-header"
      :style="{ backgroundImage: `url(${headerImage})` }"
    >
      <h1 v-if="translatedTitle">✍️ {{ translatedTitle }}</h1>
      <h1 v-else>Documentation pour les Contributeurs</h1>
      <p class="p-header">
        {{
          translatedDescription ||
          "Découvrez les bonnes pratiques et processus pour contribuer efficacement à Longoka."
        }}
      </p>
    </header>

    <!-- ✅ Introduction -->
    <section>
      <h2><i class="fas fa-info-circle"></i> Introduction</h2>
      <p>
        Longoka permet aux contributeurs d'ajouter et de modifier du contenu
        pour enrichir la plateforme. Chaque modification est soumise à
        validation avant publication.
      </p>
    </section>

    <!-- ✅ Guide de Contribution -->
    <section>
      <h2><i class="fas fa-edit"></i> Comment Contribuer</h2>
      <ul>
        <li>
          📌 <strong>Ajout de Contenu :</strong> Respectez la structure et les
          guidelines définies.
        </li>
        <li>
          ✅ <strong>Validation :</strong> Toute contribution passe par un
          processus de validation.
        </li>
        <li>
          📝 <strong>Bonnes Pratiques :</strong> Veillez à l'exactitude et à la
          pertinence des informations.
        </li>
      </ul>
    </section>

    <!-- ✅ Ressources pour Contributeurs -->
    <section>
      <h2><i class="fas fa-book"></i> Ressources Utiles</h2>
      <p>
        Consultez les documents et tutoriels mis à votre disposition pour une
        meilleure prise en main.
      </p>
      <NuxtLink to="/documentation/contributor/guidelines" class="cta-button">
        📖 Consulter les Guidelines
      </NuxtLink>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { useNuxtApp } from "#imports";
import { useHead } from "#app";

const { $translate } = useNuxtApp();
const translatedTitle = ref("");
const translatedDescription = ref("");
const headerImage = useState("headerImage");

async function translateSEO() {
  try {
    const userLang = navigator.language.split("-")[0];
    const targetLang = [
      "fr",
      "en",
      "es",
      "de",
      "it",
      "pt",
      "ru",
      "zh",
    ].includes(userLang)
      ? userLang
      : "fr";

    translatedTitle.value = await $translate(
      "Documentation pour les Contributeurs",
      "fr",
      targetLang
    );
    translatedDescription.value = await $translate(
      "Découvrez les bonnes pratiques et processus pour contribuer efficacement à Longoka.",
      "fr",
      targetLang
    );
  } catch (error) {
    console.error("❌ Erreur de traduction SEO :", error);
  }
}

onMounted(async () => {
  await translateSEO();
});

useHead(() => ({
  title: computed(() => `${translatedTitle.value} | Longoka`),
  meta: [
    { name: "description", content: translatedDescription.value },
    { name: "robots", content: "index, follow" },
    { property: "og:title", content: translatedTitle.value },
    { property: "og:description", content: translatedDescription.value },
    { property: "og:image", content: "/images/documentation-contributor.jpg" },
    { name: "application-name", content: "Longoka" },
    { name: "theme-color", content: "#1e88e5" },
  ],
}));
</script>

<style scoped>
.cta-button {
  display: inline-block;
  padding: 10px 20px;
  background: #1e88e5;
  color: white;
  border-radius: 5px;
  text-decoration: none;
  font-weight: bold;
  transition: 0.3s;
}
.cta-button:hover {
  background: #1565c0;
}
</style>
